/**
 * PDF Export Utility
 * Provides PDF export functionality using jsPDF and html2canvas
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Export HTML element to PDF
 * @param {HTMLElement} element - Element to export
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportElementToPDF = async (element, filename = 'export', options = {}) => {
  const {
    format = 'a4',
    orientation = 'portrait',
    margin = 10,
    quality = 1.0,
    scale = 2
  } = options;

  try {
    // Create canvas from element
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png', quality);
    const imgWidth = format === 'a4' ? 210 : 297; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    const pdf = new jsPDF({
      orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
      unit: 'mm',
      format
    });

    const pageHeight = pdf.internal.pageSize.height;
    let heightLeft = imgHeight;
    let position = margin;

    // Add first page
    pdf.addImage(imgData, 'PNG', margin, position, imgWidth - (margin * 2), imgHeight);
    heightLeft -= pageHeight - (margin * 2);

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth - (margin * 2), imgHeight);
      heightLeft -= pageHeight - (margin * 2);
    }

    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};

/**
 * Export data table to PDF
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportTableToPDF = (data, columns, filename = 'export', options = {}) => {
  const {
    title = 'Data Export',
    format = 'a4',
    orientation = 'portrait',
    margin = 10,
    fontSize = 10
  } = options;

  try {
    const pdf = new jsPDF({
      orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
      unit: 'mm',
      format
    });

    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;
    const usableWidth = pageWidth - (margin * 2);
    const usableHeight = pageHeight - (margin * 2) - 20; // Reserve space for title

    // Add title
    pdf.setFontSize(16);
    pdf.text(title, margin, margin + 10);

    // Calculate column widths
    const colCount = columns.length;
    const colWidth = usableWidth / colCount;

    // Add headers
    pdf.setFontSize(fontSize);
    pdf.setFont(undefined, 'bold');
    let yPos = margin + 20;
    
    columns.forEach((col, index) => {
      const xPos = margin + (index * colWidth);
      pdf.text(col.label || col.key, xPos, yPos);
    });

    // Add data rows
    pdf.setFont(undefined, 'normal');
    yPos += 7;
    
    data.forEach((row, rowIndex) => {
      // Check if we need a new page
      if (yPos > pageHeight - margin - 10) {
        pdf.addPage();
        yPos = margin + 10;
        
        // Redraw headers
        pdf.setFont(undefined, 'bold');
        columns.forEach((col, index) => {
          const xPos = margin + (index * colWidth);
          pdf.text(col.label || col.key, xPos, yPos);
        });
        pdf.setFont(undefined, 'normal');
        yPos += 7;
      }

      columns.forEach((col, colIndex) => {
        const xPos = margin + (colIndex * colWidth);
        const value = getNestedValue(row, col.key);
        const displayValue = formatValue(value, col.format);
        
        // Truncate if too long
        const maxWidth = colWidth - 2;
        const text = pdf.splitTextToSize(String(displayValue || ''), maxWidth);
        pdf.text(text, xPos, yPos);
      });

      yPos += 7;
    });

    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error exporting table to PDF:', error);
    throw error;
  }
};

/**
 * Export report to PDF with formatted content
 * @param {Object} reportData - Report data object
 * @param {string} filename - Filename (without extension)
 * @param {Object} options - Export options
 */
export const exportReportToPDF = (reportData, filename = 'report', options = {}) => {
  const {
    title = 'Analytics Report',
    format = 'a4',
    orientation = 'portrait',
    margin = 15
  } = options;

  try {
    const pdf = new jsPDF({
      orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
      unit: 'mm',
      format
    });

    const pageWidth = pdf.internal.pageSize.width;
    const usableWidth = pageWidth - (margin * 2);
    let yPos = margin;

    // Add title
    pdf.setFontSize(20);
    pdf.setFont(undefined, 'bold');
    pdf.text(title, margin, yPos);
    yPos += 10;

    // Add date
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPos);
    yPos += 10;

    // Add sections
    Object.keys(reportData).forEach(sectionKey => {
      const section = reportData[sectionKey];
      
      if (typeof section === 'object' && section !== null) {
        // Section header
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text(sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1), margin, yPos);
        yPos += 8;

        // Section content
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        
        Object.keys(section).forEach(key => {
          const value = section[key];
          const displayValue = typeof value === 'object' 
            ? JSON.stringify(value, null, 2)
            : String(value);
          
          const lines = pdf.splitTextToSize(`${key}: ${displayValue}`, usableWidth);
          lines.forEach(line => {
            if (yPos > pdf.internal.pageSize.height - margin - 10) {
              pdf.addPage();
              yPos = margin;
            }
            pdf.text(line, margin + 5, yPos);
            yPos += 5;
          });
        });

        yPos += 5;
      }
    });

    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error exporting report to PDF:', error);
    throw error;
  }
};

/**
 * Format value based on column format
 * @param {*} value - Value to format
 * @param {string} format - Format type
 * @returns {string} - Formatted value
 */
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '';
  
  switch (format) {
    case 'currency':
      return typeof value === 'number' 
        ? `$${value.toFixed(2)}` 
        : `$${parseFloat(value || 0).toFixed(2)}`;
    case 'percentage':
      return typeof value === 'number' 
        ? `${(value * 100).toFixed(2)}%` 
        : `${(parseFloat(value || 0) * 100).toFixed(2)}%`;
    case 'date':
      return value instanceof Date 
        ? value.toLocaleDateString() 
        : new Date(value).toLocaleDateString();
    case 'number':
      return typeof value === 'number' 
        ? value.toLocaleString() 
        : parseFloat(value || 0).toLocaleString();
    default:
      return String(value);
  }
};

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path
 * @returns {*} - Value at path or null
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return null;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) {
      return null;
    }
    value = value[key];
  }
  
  return value;
};




